import { useEffect, useState } from 'react';
import { formatNumbers } from '../../utils/convert-to-arabic-price';
import { Product } from '../../types/product';
import { Filter, searchProducts } from '../../api/searchProducts';
import { ToastType } from '../../types/toast';
import VerticalFilters from '../VerticalFilters';
import { getSearchBarSettings, Settings } from '../../api/getSearchBarSettings';
import NoProductsIcon from '../../assets/no-products.svg?react';
import { useDebouncedCallback } from 'use-debounce';
import { safeTrack } from '../../utils/analytics';
import { ENTERED_FROM_SEARCH_PAGE } from '../../constants/tracking';
import {
  setTrackingSource,
  setDirectTrackingFlag,
} from '../../utils/tracking-session';
import ProductCard from '../ProductCard';
import {
  getProductCardSettings,
  ProductCardSettings,
} from '../../api/product-card';
import { addToCartTracking } from '../../utils/cart-tracking';

interface SearchPageProductsProps {
  store: string;
  customer: string;
  provider: string;
  color: string;
  currency: string;
  language: string;
  locale: string;
}

const SearchPageProducts: React.FC<SearchPageProductsProps> = ({
  store,
  customer,
  provider,
  color,
  currency = 'SAR',
  language = 'ar-SA',
  locale = 'ar',
}) => {
  const primaryColor = color === '#ffffff' ? '#000000' : color;

  const getSearchQuery = (
    provider: string,
  ): { query: string; imageSearchUrl: string } => {
    const searchParams = new URLSearchParams(window.location.search);
    let query = '';
    const imageSearchUrl = searchParams.get('image') || '';
    switch (provider) {
      case 'zid': {
        query = searchParams.get('search')?.replace(/\__/g, '') || '';
        break;
      }
      case 'salla': {
        query = searchParams.get('q')?.replace(/\__/g, '') || '';
        break;
      }
      case 'woocommerce': {
        const normalQuery = searchParams.get('s')?.replace(/\__/g, '') || '';
        const pathParts = window.location.pathname.split('/');
        const searchIndex = pathParts.indexOf('search');
        const permalinkQuery =
          searchIndex !== -1 && searchIndex + 1 < pathParts.length
            ? pathParts[searchIndex + 1].replace(/\__/g, '')
            : '';
        query =
          normalQuery ||
          permalinkQuery ||
          searchParams.get('search')?.replace(/\__/g, '') ||
          '';
        break;
      }
      default: {
        query = '';
        break;
      }
    }
    return { query, imageSearchUrl };
  };

  const query = getSearchQuery(provider);
  const [cardSettings, setCardSettings] = useState<ProductCardSettings | null>(
    null,
  );
  const [searchConfig, setSearchConfig] = useState<Settings | null>(null);

  const [products, setProducts] = useState<Product[]>([]);
  const [meta, setMeta] = useState<{
    pageNumber: number;
    pageSize: number;
    prevPage?: number;
    nextPage?: number;
    total: number;
    totalPages: number;
  } | null>(null);
  const [favorites, setFavorites] = useState<string[]>([]);
  const [loadingStates, setLoadingStates] = useState<Record<string, boolean>>(
    {},
  );
  const [dynamicFilters, setDynamicFilters] = useState<Filter[]>([]);
  const [appliedFilters, setAppliedFilters] = useState<
    Record<string, string[] | { from: number; to: number }>
  >({});
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [sort, setSort] = useState('best');
  const [sortDir, setSortDir] = useState('DESC');
  const [taxRate, setTaxRate] = useState<number>(1);
  const [isFilterLoading, setIsFilterLoading] = useState(false);
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);
  const [currentRequest, setCurrentRequest] = useState<number | null>(null);
  const [isLastPage, setIsLastPage] = useState(false);

  const getCardSettings = async () => {
    const { uiStyles } = await getProductCardSettings(store, provider);
    setCardSettings({ uiStyles });
  };

  const getSettings = async () => {
    const settings = await getSearchBarSettings(store, provider);
    setSearchConfig(settings);
  };

  const showToast = (message: string, type: ToastType) => {
    const event = new CustomEvent('show-toast', { detail: { message, type } });
    window.dispatchEvent(event);
  };

  const quickAddToSallaFavorites = async (productId: number) => {
    await window.salla?.api.wishlist.add(productId);
  };

  const quickAddToZidFavorites = async (productId: number) => {
    const addtoFavoritesResponse = await fetch(
      `/api/v1/customer/addToWishlist`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ product_id: productId }),
      },
    );

    const addtoFavoritesRes = await addtoFavoritesResponse.json();
    return addtoFavoritesRes;
  };

  const removeFromZidFavorites = async (productId: number) => {
    const removeFromFavoritesResponse = await fetch(
      `/api/v1/customer/removeFromWishlist`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ product_id: productId }),
      },
    );

    const removeFromFavoritesRes = await removeFromFavoritesResponse.json();
    return removeFromFavoritesRes;
  };

  const quickAddToCart = async (product: Product, quantity: number) => {
    setLoadingStates((prev) => ({ ...prev, [product.productId]: true }));
    setDirectTrackingFlag(); // Set flag BEFORE API call to prevent ProductView duplicate
    addToCartTracking(product.name.ar, 'search_page');
    await window.salla?.api.cart.quickAdd(product.productId, quantity);
    safeTrack('product_added_to_cart', {
      query: query.query || '',
      storeId: store,
      provider: provider,
      customerId: customer,
      product: product.productId,
      productName: product.name.ar,
      type: 'search_page',
      reached_from: 'search_page',
    });
    setLoadingStates((prev) => ({ ...prev, [product.productId]: false }));
  };

  const quickAddToZidCart = async (product: Product) => {
    setLoadingStates((prev) => ({ ...prev, [product.productId]: true }));
    const has_vitrin = window.vitrin == true;

    const productDetails = has_vitrin
      ? await window.zid?.products.get(String(product.productId))
      : await window.zid?.store.product.fetch(String(product.productId));

    const variants = has_vitrin
      ? productDetails.variants
      : productDetails.data.product.variants;
    if (variants && variants.length > 0) {
      safeTrack('product_clicked', {
        query: query.query || '',
        storeId: store,
        provider: provider,
        customerId: customer,
        product: product.productId,
        type: 'search_page',
        page: page,
        productName: product.name.ar,
      });

      setTrackingSource(ENTERED_FROM_SEARCH_PAGE, product.productId.toString());

      window.open(
        locale === 'ar' ? product.urlTrans?.ar : product.urlTrans?.en,
        '_self',
      );
      setLoadingStates((prev) => ({ ...prev, [product.productId]: false }));
    } else {
      setDirectTrackingFlag(); // Set flag BEFORE API call to prevent ProductView duplicate
      addToCartTracking(product.name.ar, 'search_page');
      try {
        const addtoCartResponse = has_vitrin
          ? await window?.zid?.cart.addProduct({
              product_id: product.productId,
              quantity: 1,
            })
          : await window?.zid?.store.cart.addProduct({
              productId: product.productId,
              quantity: 1,
            });

        // Check if the response indicates success (has 'item' property)
        if (addtoCartResponse.item || addtoCartResponse.status === 'success') {
          showToast('تمت إضافة المنتج بنجاح إلى عربة التسوق', 'success');
          safeTrack('product_added_to_cart', {
            query: query.query || '',
            storeId: store,
            provider: provider,
            customerId: customer,
            product: product.productId,
            productName: product.name.ar,
            type: 'search_page',
            page: page,
          });
        } else {
          showToast('حدث خطأ أثناء إضافة المنتج إلى السلة', 'error');
        }
      } catch (error) {
        console.error('Error adding to cart:', error);
        showToast('حدث خطأ أثناء إضافة المنتج إلى السلة', 'error');
      } finally {
        setLoadingStates((prev) => ({ ...prev, [product.productId]: false }));
      }
    }
  };

  const quickAddToWoocommerceCart = async (
    product: Product,
    quantity: number,
  ) => {
    setLoadingStates((prev) => ({ ...prev, [product.productId]: true }));
    setDirectTrackingFlag(); // Set flag BEFORE API call to prevent ProductView duplicate
    addToCartTracking(product.name.ar, 'search_page');
    try {
      const response = await fetch('/?wc-ajax=add_to_cart', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: `product_id=${product.productId}&quantity=${quantity}`,
      });

      const data = await response.json();

      if (data.error) {
        console.error('Error adding to cart:', data.message);
      } else {
        safeTrack('product_added_to_cart', {
          query: query.query || '',
          storeId: store,
          provider: provider,
          customerId: customer,
          product: product.productId,
          productName: product.name.ar,
          type: 'search_page',
          page: page,
        });
        console.log('Product added to cart:', data);

        // Trigger WooCommerce's `added_to_cart` event
        document.dispatchEvent(
          new CustomEvent('added_to_cart', { detail: product.productId }),
        );

        // Fetch updated cart fragments and update UI
        const cartFragmentResponse = await fetch(
          '/?wc-ajax=get_refreshed_fragments',
        );
        const cartFragmentData = await cartFragmentResponse.json();

        if (cartFragmentData && cartFragmentData.fragments) {
          for (const [selector, html] of Object.entries(
            cartFragmentData.fragments,
          )) {
            const element = document.querySelector(selector);
            if (element) {
              element.innerHTML = html as string; // Replace the old fragment with the new one
            }
          }
        }
      }
    } catch (error) {
      console.error('Request failed:', error);
    } finally {
      setLoadingStates((prev) => ({ ...prev, [product.productId]: false }));
    }
  };

  // const fetchZidWishlist = async () => {
  //   await window.zid.store.customer.fetchWishlist().then((response) => {
  //     const products = response.data.wishlist.data;
  //     const productsIds = products.map((item) => item.id);
  //     setFavorites(productsIds);
  //   });
  // };

  const toggleFavorites = async (product: Product) => {
    if (provider === 'salla') {
      await quickAddToSallaFavorites(product.productId);
      safeTrack('product_added_to_favorites', {
        query: query.query || '',
        storeId: store,
        provider: provider,
        customerId: customer,
        type: 'search_page',
        productName: product.name.ar,
        page: page,
      });
      return;
    }
    if (provider === 'zid') {
      if (customer === 'undefined' || customer === undefined) {
        window.open('/auth/login', '_self');
        return;
      }
      if (favorites.includes(product.productId.toString())) {
        const res = await removeFromZidFavorites(product.productId);
        if (res.status === 'success') {
          showToast(res.data.message, 'success');
          setFavorites((prev) =>
            prev.filter((id) => id !== product.productId.toString()),
          );
        } else {
          showToast(res.data.message, 'error');
        }
      } else {
        const res = await quickAddToZidFavorites(product.productId);

        if (res.status === 'success') {
          showToast(res.data.message, 'success');
          setFavorites((prev) => [...prev, product.productId.toString()]);
        } else {
          showToast(res.data.message, 'error');
        }
      }
    }
  };

  const handleAddToCart = async (product: Product) => {
    provider === 'salla'
      ? quickAddToCart(product, 1)
      : provider === 'zid'
      ? quickAddToZidCart(product)
      : quickAddToWoocommerceCart(product, 1);
  };

  const getProductCount = (count: number, locale: string): string => {
    if (count === 0) return '';
    if (count === 1)
      return ` - ${formatNumbers(count, language as 'ar-SA' | 'en-US')} ${
        locale === 'ar' ? 'منتج' : 'product'
      }`;
    if (count === 2)
      return ` - ${formatNumbers(count, language as 'ar-SA' | 'en-US')} ${
        locale === 'ar' ? 'منتجان' : 'products'
      }`;
    if (count >= 3 && count <= 10)
      return ` - ${formatNumbers(count, language as 'ar-SA' | 'en-US')} ${
        locale === 'ar' ? 'منتجات' : 'products'
      }`;
    return ` - ${formatNumbers(count, language as 'ar-SA' | 'en-US')} ${
      locale === 'ar' ? 'منتج' : 'product'
    }`;
  };

  const fetchProducts = async (
    currentPage = 1,
    append = false,
    orderBy: string = 'best',
    orderDir = '',
  ) => {
    // Don't proceed if we're trying to fetch a page beyond the total pages
    if (meta?.totalPages && currentPage > meta.totalPages) {
      setIsLastPage(true);
      setHasMore(false);
      return;
    }

    if (append) {
      setIsLoadingMore(true);
    }

    try {
      const hasDecimals = (num: number) => num % 1 !== 0;

      const transformedFilters =
        appliedFilters && Object.keys(appliedFilters).length > 0
          ? {
              filters: Object.entries(appliedFilters).map(([key, value]) => {
                if (key === 'price') {
                  const priceValue = value as { from: number; to: number };
                  const priceFilter = dynamicFilters.find(
                    (filter) => filter.key === 'price',
                  );

                  // Get original min/max values from backend (without taxes)
                  const originalMin = priceFilter?.min || 0;
                  const originalMax = priceFilter?.max || 0;

                  // Calculate prices without tax
                  const fromWithoutTax = priceValue.from / taxRate;
                  const toWithoutTax = priceValue.to / taxRate;

                  // If calculated value is very close to min/max, use the exact min/max
                  const threshold = 0.01; // 1 cent threshold
                  const from =
                    Math.abs(fromWithoutTax - originalMin) < threshold
                      ? originalMin
                      : hasDecimals(fromWithoutTax)
                      ? Number(fromWithoutTax.toFixed(2))
                      : Number(fromWithoutTax.toFixed());

                  const to =
                    Math.abs(toWithoutTax - originalMax) < threshold
                      ? originalMax
                      : hasDecimals(toWithoutTax)
                      ? Number(toWithoutTax.toFixed(2))
                      : Number(toWithoutTax.toFixed());

                  return {
                    key,
                    from: from.toString(),
                    to: to.toString(),
                  };
                }
                return {
                  key,
                  values: value as string[],
                };
              }),
            }
          : undefined;

      const response = await searchProducts({
        storeId: store,
        ...(query.query ? { query: query.query } : {}),
        ...(query.imageSearchUrl ? { image: query.imageSearchUrl } : {}),
        storeType: provider,
        limit: 10,
        page: currentPage,
        filters: transformedFilters,
        currency,
        orderBy,
        language: locale,
        orderDir: orderDir.length ? orderDir : undefined,
      });

      if (response.tax) {
        setTaxRate(response.tax);
      }

      if (append) {
        setProducts((prev) => [...prev, ...response.results.products]);
      } else {
        setProducts(response.results.products);
      }

      if (!dynamicFilters.length) {
        setDynamicFilters(response.results.filters);
      }
      setMeta(response.meta);
      setIsLastPage(currentPage >= response.meta.totalPages);
      setHasMore(
        response.results.products.length === 10 &&
          currentPage < response.meta.totalPages,
      );
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      if (append) {
        setIsLoadingMore(false);
      }
      setIsInitialLoading(false);
    }
  };

  const transformFilters = (
    filters: Filter[],
  ): {
    price?: {
      min: number;
      max: number;
      value: { from: number; to: number };
    };
    categories?: {
      options: { value: string; count: number }[];
      value: string[];
    };
    brand?: {
      options: { value: string; count: number }[];
      value: string[];
    };
    color?: {
      options: { value: string; count: number }[];
      value: string[];
    };
    material?: {
      options: { value: string; count: number }[];
      value: string[];
    };
    size?: {
      options: { value: string; count: number }[];
      value: string[];
    };
    style?: {
      options: { value: string; count: number }[];
      value: string[];
    };
  } => {
    const transformed = filters.reduce((acc, filter) => {
      if (filter.key === 'price') {
        acc.price = {
          min: filter.min || 0,
          max: filter.max || 0,
          value: (appliedFilters['price'] as { from: number; to: number }) || {
            from: filter.min || 0,
            to: filter.max || 0,
          },
        };
      } else {
        // Handle all checkbox-type filters generically
        const filterKey = filter.key as keyof typeof acc;
        if (filterKey !== 'price') {
          (acc as any)[filterKey] = {
            options: filter.values || [],
            value: (appliedFilters[filter.key] as string[]) || [],
          };
        }
      }
      return acc;
    }, {} as ReturnType<typeof transformFilters>);

    return transformed;
  };

  const handleFilterChange = (
    key: string,
    value: string[] | { from: number; to: number },
  ) => {
    safeTrack('search_page_filter_changed', {
      query: query.query,
      storeId: store,
      provider: provider,
      customerId: customer,
      type: 'search_page',
      key: key,
    });
    setProducts([]); // Reset products array before applying new filters
    setIsFilterLoading(true); // Set loading state when filter changes
    setAppliedFilters((prev) => {
      // If value is an array (categories) and it's empty, remove the key
      if (Array.isArray(value) && value.length === 0) {
        const { [key]: _, ...rest } = prev;
        return rest;
      }

      // Otherwise, update the filter as before
      return {
        ...prev,
        [key]: value,
      };
    });
  };

  const handleScroll = useDebouncedCallback(() => {
    // Get the products container element
    const productsContainer = document.querySelector(
      '.alfinder-products-list-wrapper.alfinder-products-list-vertical-cards',
    );

    if (!productsContainer) {
      // Fallback to document-based calculation if container not found
      const scrollTop =
        window.pageYOffset || document.documentElement.scrollTop;
      const windowHeight = window.innerHeight;
      const documentHeight = Math.max(
        document.body.scrollHeight,
        document.documentElement.scrollHeight,
        document.body.offsetHeight,
        document.documentElement.offsetHeight,
        document.body.clientHeight,
        document.documentElement.clientHeight,
      );

      const threshold = 300;
      const scrollPosition = scrollTop + windowHeight;
      const triggerPoint = documentHeight - threshold;

      if (scrollPosition >= triggerPoint) {
        fetchMoreProducts();
      }
      return;
    }

    // Calculate based on the products container
    const containerRect = productsContainer.getBoundingClientRect();
    const containerBottom = containerRect.bottom + window.pageYOffset;
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const windowHeight = window.innerHeight;
    const scrollPosition = scrollTop + windowHeight;

    // Use a smaller threshold since we're measuring against the actual container
    const threshold = 200;
    const triggerPoint = containerBottom - threshold;

    console.log('Scroll check:', {
      containerBottom,
      scrollPosition,
      triggerPoint,
      threshold,
      shouldTrigger: scrollPosition >= triggerPoint,
    });

    if (scrollPosition >= triggerPoint) {
      fetchMoreProducts();
    }
  }, 100); // Reduced debounce to 100ms for better responsiveness

  const fetchMoreProducts = () => {
    // Add debugging for troubleshooting
    console.log('fetchMoreProducts called:', {
      isLoadingMore,
      hasMore,
      isLastPage,
      currentPage: page,
      totalPages: meta?.totalPages,
      currentRequest,
    });

    // Don't fetch if we're already loading, have no more pages, or at the last page
    if (isLoadingMore || !hasMore || isLastPage) {
      console.log('fetchMoreProducts blocked:', {
        isLoadingMore,
        hasMore,
        isLastPage,
      });
      return;
    }

    const nextPage = page + 1;

    // Don't proceed if we're already fetching this page
    if (currentRequest === nextPage) {
      console.log(
        'fetchMoreProducts blocked: already requesting page',
        nextPage,
      );
      return;
    }

    // Don't fetch if we've exceeded total pages
    if (meta && nextPage > meta.totalPages) {
      console.log(
        'fetchMoreProducts blocked: exceeded total pages',
        nextPage,
        meta.totalPages,
      );
      setIsLastPage(true);
      setHasMore(false);
      return;
    }

    console.log('fetchMoreProducts: fetching page', nextPage);
    setCurrentRequest(nextPage);
    setPage(nextPage);
    fetchProducts(nextPage, true, sort, sortDir).finally(() => {
      setCurrentRequest(null);
    });
    safeTrack('search_page_fetch_more_products', {
      query: query.query || '',
      storeId: store,
      provider: provider,
      customerId: customer,
      type: 'search_page',
      page: nextPage,
    });
  };

  const handleSortChange = async (value: string) => {
    setPage(1);
    setProducts([]);
    setIsFilterLoading(true);
    if (value === 'price_desc') {
      setSort('price');
      setSortDir('DESC');
      fetchProducts(1, false, 'price', 'DESC').finally(() => {
        setIsFilterLoading(false);
      });
    } else if (value === 'price_asc') {
      setSort('price');
      setSortDir('ASC');
      fetchProducts(1, false, 'price', 'ASC').finally(() => {
        setIsFilterLoading(false);
      });
    } else {
      setSort(value);
      setSortDir('');
      fetchProducts(1, false, value).finally(() => {
        setIsFilterLoading(false);
      });
    }
  };

  const isDarkMode = () => {
    const htmlElement = document.documentElement;
    return htmlElement.classList.contains('dark');
  };

  // Initial fetch only
  useEffect(() => {
    fetchProducts(1, false, sort, sortDir);
    getCardSettings();
    getSettings();
    safeTrack('search_page_opened', {
      query: query.query,
      storeId: store,
      provider: provider,
      customerId: customer,
      type: 'search_page',
    });
  }, []); // Empty dependency array means this runs once on mount

  // Subsequent fetches for filters/query changes
  useEffect(() => {
    setPage(1);
    setHasMore(true);
    setProducts([]); // Reset products array when filters change
    if (!isInitialLoading) {
      // Only fetch if initial loading is done
      fetchProducts(1, false, sort, sortDir).finally(() => {
        setIsFilterLoading(false); // Reset loading state after fetch completes
      });
    }
  }, [appliedFilters, query.query, query.imageSearchUrl]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      handleScroll.cancel(); // Cancel any pending debounced calls
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]); // Only depend on handleScroll to avoid unnecessary re-registrations

  return (
    <div className="search-results">
      <div
        style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem',
        }}
      >
        {provider === 'salla' ? (
          <div
            style={{
              fontSize: '1.25rem',
              lineHeight: '1.75rem',
              fontWeight: 700,
              color: isDarkMode() ? '#fff' : '#000',
              display: 'flex',
            }}
          >
            {locale === 'ar' ? 'البحث عن' : 'Search for'} (
            {query.query.length ? (
              query.query
            ) : (
              <img
                src={query.imageSearchUrl}
                alt="Search image"
                style={{
                  width: '30px',
                  height: '30px',
                  borderRadius: '4px',
                  objectFit: 'cover',
                  border: '1px solid #e0e0e0',
                }}
              />
            )}
            ){getProductCount(meta?.total || 0, locale)}
          </div>
        ) : (
          <div
            style={{
              fontSize: '1rem',
              lineHeight: '1.75rem',
              color: isDarkMode() ? '#fff' : '#000',
            }}
          >
            {locale === 'ar' ? 'تم ايجاد' : 'Found'}{' '}
            {getProductCount(meta?.total || 0, locale)}
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div
            style={{
              fontSize: '15px',
              lineHeight: '26px',
              fontWeight: 300,
              marginInlineEnd: '0.5rem',
              color: isDarkMode() ? '#fff' : '#000',
            }}
          >
            {locale === 'ar' ? 'ترتيب' : 'Sort'}
          </div>
          <div>
            <select
              defaultValue={sort}
              onChange={(e) => {
                handleSortChange(e.target.value);
              }}
              style={
                {
                  '--alfinder-border-color': primaryColor,
                } as React.CSSProperties
              }
              className="alfinder-search-page-sort-select"
            >
              <option value="best">
                {locale === 'ar' ? 'الاقرب صلة' : 'Closest Match'}
              </option>
              <option value="new">
                {' '}
                {locale === 'ar' ? 'الأحدث' : 'Newest'}
              </option>
              <option value="price_desc">
                {' '}
                {locale === 'ar'
                  ? 'السعر من الاعلى إلى الاقل'
                  : 'Price from Highest to Lowest'}
              </option>
              <option value="price_asc">
                {' '}
                {locale === 'ar'
                  ? 'السعر من الاقل إلى الاعلى'
                  : 'Price from Lowest to Highest'}
              </option>
            </select>
          </div>
        </div>
      </div>
      {isInitialLoading ? (
        <div
          style={{
            gridColumn: '1/-1',
            display: 'flex',
            justifyContent: 'center',
            padding: '20px',
          }}
        >
          <span
            className="loader-alfinder-fetch-more"
            style={{
              border: `3px solid ${primaryColor}`,
              borderBottomColor: 'transparent',
              borderRadius: '50%',
            }}
          ></span>
        </div>
      ) : products.length === 0 && dynamicFilters.length === 0 ? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
          }}
        >
          <div className="no-products-icon-container">
            <NoProductsIcon />
          </div>
          <div style={{ color: '#d1d5db' }}>لا توجد منتجات</div>
        </div>
      ) : (
        <div className="search-page-container">
          {window.innerWidth <= 768 && (
            <button
              onClick={() => setIsMobileFiltersOpen(true)}
              className="mobile-filters-button"
              style={{
                backgroundColor: primaryColor,
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '8px',
                marginBottom: '15px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <i className="sicon-filter"></i>
              تصفية المنتجات
            </button>
          )}
          <div style={{ display: 'flex', gap: '20px' }}>
            <VerticalFilters
              filters={transformFilters(dynamicFilters)}
              primaryColor={primaryColor}
              onFilterChange={handleFilterChange}
              isMobileFiltersOpen={isMobileFiltersOpen}
              onCloseMobileFilters={() => setIsMobileFiltersOpen(false)}
              currency={currency}
              isDarkMode={isDarkMode()}
              language={language}
              availableFilters={
                searchConfig?.uiStyles?.allowedDynamicFilters || [
                  'price',
                  'categories',
                ]
              }
              locale={locale}
            />
            {isFilterLoading ? (
              <div
                style={{
                  gridColumn: '1/-1',
                  display: 'flex',
                  justifyContent: 'center',
                  padding: '20px',
                }}
              >
                <span
                  className="loader-alfinder-fetch-more"
                  style={{
                    border: `3px solid ${primaryColor}`,
                    borderBottomColor: 'transparent',
                    borderRadius: '50%',
                  }}
                ></span>
              </div>
            ) : products.length > 0 ? (
              <div
                className="alfinder-products-list-wrapper alfinder-products-list-vertical-cards"
                style={
                  {
                    '--alfinder-product-card-width':
                      cardSettings?.uiStyles?.productCardWidth,
                  } as React.CSSProperties
                }
              >
                {products.map((product) => (
                  // <div
                  //   key={product.id}
                  //   className="alfinder-product-card-entry alfinder-product-card-vertical"
                  // >
                  //   <div className="alfinder-product-card-image">
                  //     <a
                  //       href={product.url}
                  //       onClick={async (e) => {
                  //         e.preventDefault();
                  //         safeTrack('product_clicked', {
                  //           query: query,
                  //           storeId: store,
                  //           provider: provider,
                  //           customerId: customer,
                  //           product: product.productId,
                  //           type: 'search_page',
                  //           page: page,
                  //           productName: product.name.ar,
                  //         });
                  //         sessionStorage.setItem(
                  //           CLICKED_PRODUCT_ID,
                  //           product.productId.toString(),
                  //         );

                  //         await handleProductClick(product);
                  //         window.open(product.url, '_self');
                  //       }}
                  //     >
                  //       <img
                  //         className="alfinder-product-card-image-cover lazy loaded"
                  //         src={product.mainImage}
                  //         alt={product.name.ar}
                  //         loading="lazy"
                  //       />
                  //     </a>
                  //     {provider === 'salla' ? (
                  //       <button
                  //         color="light"
                  //         className="alfinder-product-card-wishlist-btn"
                  //         aria-label="Add or remove to wishlist"
                  //         onClick={() => toggleFavorites(product)}
                  //       >
                  //         <button className="wishlist-icon-button">
                  //           <span className="wishlist-icon-wrapper">
                  //             <i className="sicon-heart"></i>
                  //           </span>
                  //         </button>
                  //       </button>
                  //     ) : (
                  //       <button
                  //         className="alfinder-wishlist-icon-button"
                  //         aria-label="Add or remove to wishlist"
                  //         onClick={() => toggleFavorites(product)}
                  //       >
                  //         <span className="alfinder-wishlist-icon-wrapper">
                  //           <svg
                  //             width="20px"
                  //             height="20px"
                  //             viewBox="0 0 24 24"
                  //             fill="none"
                  //             xmlns="http://www.w3.org/2000/svg"
                  //           >
                  //             <path
                  //               d="M4.45067 13.9082L11.4033 20.4395C11.6428 20.6644 11.7625 20.7769 11.9037 20.8046C11.9673 20.8171 12.0327 20.8171 12.0963 20.8046C12.2375 20.7769 12.3572 20.6644 12.5967 20.4395L19.5493 13.9082C21.5055 12.0706 21.743 9.0466 20.0978 6.92607L19.7885 6.52734C17.8203 3.99058 13.8696 4.41601 12.4867 7.31365C12.2913 7.72296 11.7087 7.72296 11.5133 7.31365C10.1304 4.41601 6.17972 3.99058 4.21154 6.52735L3.90219 6.92607C2.25695 9.0466 2.4945 12.0706 4.45067 13.9082Z"
                  //               stroke="#000"
                  //               stroke-width="2"
                  //             />
                  //           </svg>
                  //         </span>
                  //       </button>
                  //     )}
                  //   </div>

                  //   <div className="alfinder-product-card-content">
                  //     <div className="alfinder-product-card-content-main">
                  //       <h3 className="alfinder-product-card-content-title">
                  //         <a
                  //           href={product.url}
                  //           onClick={async (e) => {
                  //             e.preventDefault();
                  //             safeTrack('product_clicked', {
                  //               query: query,
                  //               storeId: store,
                  //               provider: provider,
                  //               customerId: customer,
                  //               product: product.productId,
                  //               type: 'search_page',
                  //               page: page,
                  //               productName: product.name.ar,
                  //             });
                  //             sessionStorage.setItem(
                  //               CLICKED_PRODUCT_ID,
                  //               product.productId.toString(),
                  //             );
                  //             await handleProductClick(product);
                  //             window.open(product.url, '_self');
                  //           }}
                  //         >
                  //           {product.name.ar}
                  //         </a>
                  //       </h3>
                  //     </div>

                  //     <div className="alfinder-product-card-content-sub">
                  //       <h4 className="alfinder-product-card-price">
                  //         {product.discountedPrice ? (
                  //           <div
                  //             style={{
                  //               display: 'flex',
                  //               alignItems: 'center',
                  //             }}
                  //           >
                  //             <div
                  //               style={{
                  //                 display: 'flex',
                  //                 alignItems: 'center',
                  //                 marginInlineEnd: '6px',
                  //                 color: '#ad1a10',
                  //               }}
                  //             >
                  //               <div
                  //                 style={{
                  //                   fontSize: '0.875rem',
                  //                   marginInlineEnd: '1px',
                  //                 }}
                  //               >
                  //                 {formatNumbers(
                  //                   product.discountedPrice,
                  //                   language as 'ar-SA' | 'en-US',
                  //                   currency,
                  //                 )}
                  //               </div>
                  //               {currency === 'SAR' && (
                  //                 <RIYALIcon fill="#ad1a10" />
                  //               )}
                  //             </div>
                  //             <div
                  //               style={{
                  //                 display: 'flex',
                  //                 alignItems: 'center',
                  //                 textDecoration: 'line-through',
                  //               }}
                  //             >
                  //               <div
                  //                 style={{
                  //                   fontSize: '0.875rem',
                  //                   marginInlineEnd: '1px',
                  //                 }}
                  //               >
                  //                 {formatNumbers(
                  //                   product.price,
                  //                   language as 'ar-SA' | 'en-US',
                  //                   currency,
                  //                 )}
                  //               </div>
                  //               {currency === 'SAR' && <RIYALIcon />}
                  //             </div>
                  //           </div>
                  //         ) : (
                  //           <div
                  //             style={{ display: 'flex', alignItems: 'center' }}
                  //           >
                  //             <div
                  //               style={{
                  //                 fontSize: '0.875rem',
                  //                 marginInlineEnd: '1px',
                  //               }}
                  //             >
                  //               {formatNumbers(
                  //                 product.price,
                  //                 language as 'ar-SA' | 'en-US',
                  //                 currency,
                  //               )}
                  //             </div>
                  //             {currency === 'SAR' && <RIYALIcon />}
                  //           </div>
                  //         )}
                  //       </h4>
                  //     </div>

                  //     <div className="alfinder-product-card-content-footer">
                  //       <button
                  //         style={{
                  //           width: '100%',
                  //           border: 'none',
                  //           background: 'none',
                  //         }}
                  //       >
                  //         <div style={{ width: '100%' }}>
                  //           {product.isAvailable ? (
                  //             <button
                  //               className="custom-button"
                  //               onClick={() => {
                  //                 handleAddToCart(product);
                  //               }}
                  //               disabled={loadingStates[product.productId]}
                  //               style={
                  //                 {
                  //                   '--button-primary-color': primaryColor,
                  //                   borderColor: primaryColor,
                  //                   color: primaryColor,
                  //                 } as React.CSSProperties
                  //               }
                  //               type="button"
                  //             >
                  //               {loadingStates[product.productId] ? (
                  //                 <span
                  //                   className="loader-alfinder-add-to-cart"
                  //                   style={{
                  //                     border: `3px solid #000`,
                  //                     borderBottomColor: 'transparent',
                  //                     borderRadius: '50%',
                  //                   }}
                  //                 ></span>
                  //               ) : (
                  //                 <span>
                  //                   <i className="text-base sicon-shopping-bag"></i>{' '}
                  //                   <span>إضافة للسلة</span>
                  //                 </span>
                  //               )}
                  //             </button>
                  //           ) : (
                  //             <span
                  //               style={{
                  //                 color: '#ad1a10',
                  //                 flex: '1',
                  //                 display: 'flex',
                  //                 justifyContent: 'center',
                  //                 alignItems: 'center',
                  //                 cursor: 'default',
                  //               }}
                  //             >
                  //               <span>نفدت الكمية</span>
                  //             </span>
                  //           )}
                  //         </div>
                  //       </button>
                  //     </div>
                  //   </div>
                  // </div>
                  <ProductCard
                    product={product}
                    handleProductClick={async (product) => {
                      safeTrack('product_clicked', {
                        query: query.query || '',
                        storeId: store,
                        provider: provider,
                        customerId: customer,
                        product: product.productId,
                        type: 'search_page',
                        page: page,
                        productName: product.name.ar,
                      });

                      setTrackingSource(
                        ENTERED_FROM_SEARCH_PAGE,
                        product.productId.toString(),
                      );

                      window.open(product.url, '_self');
                    }}
                    toggleFavorites={async (product) => {
                      await toggleFavorites(product);
                    }}
                    handleAddToCart={async (product) => {
                      await handleAddToCart(product);
                    }}
                    loadingStates={loadingStates}
                    preview={false}
                    config={cardSettings?.uiStyles}
                    currency={currency}
                    showDiscounts={searchConfig?.showDiscounts}
                    language={language}
                  />
                ))}
                {isLoadingMore && (
                  <div
                    style={{
                      gridColumn: '1/-1',
                      display: 'flex',
                      justifyContent: 'center',
                      padding: '20px',
                    }}
                  >
                    <span
                      className="loader-alfinder-fetch-more"
                      style={{
                        border: `3px solid ${primaryColor}`,
                        borderBottomColor: 'transparent',
                        borderRadius: '50%',
                      }}
                    ></span>
                  </div>
                )}
              </div>
            ) : (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                  width: '100%',
                }}
              >
                <div className="no-products-icon-container">
                  <NoProductsIcon />
                </div>
                <div style={{ color: '#d1d5db' }}>لا توجد منتجات</div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchPageProducts;
